import React, { useState, useEffect, useCallback, Component, useRef, useMemo, lazy, Suspense } from 'react';
import Icon, { IconType } from '../Icon/Icon';
import { toast } from 'react-toastify';
import type ReactQuillType from 'react-quill';
import { NewGuid } from '../../utils/Tools';
import Modal from '../Modal/Modal';

const ReactQuill = lazy(() => import('./QuillEditor'));
const XEditor = lazy(() => import('../Tools/xEditor'));

interface IEditorProps {
  value: string;
  options: 'simple' | 'full';
  changed: Function;
  height: 'normal' | 'short';
  onblur?: (value?: any) => void;
  readOnly?: boolean;
  saveBeforeChange?: boolean;
  close?: () => void;
  hideExpand?: boolean;
  hideAlign?: boolean;
  placeholder?: string;
}

const Editor = (props: IEditorProps) => {
  const id = useRef(NewGuid()).current;
  const quillRef = useRef<ReactQuillType>();
  const [editorState, setEditorState] = useState<string>('');
  const [expanded, setExpanded] = useState<boolean>(false);
  const [showSource, setShowSource] = useState<boolean>(false);
  const [sourceContent, setSourceContent] = useState<string>('');

  useEffect(() => {
    if (props.value) {
      setEditorState(props.value);
    } else {
    }
  }, []);

  const onBlur = (event, editorState) => {
    if (!props.saveBeforeChange && props.onblur) props.onblur();
  };

  const savePress = () => {
    let val = editorState;
    props.changed(val);
    if (props.onblur) props.onblur(val);
  };
  const closePress = () => {
    if (props.close) props.close();
  };
  const closeSavePress = () => {
    savePress();
    closePress();
  };

  const imageClickHandler = () => {
    let editor = quillRef.current?.getEditor();
    if (editor) {
      let range = editor.getSelection(true);
      let value = prompt('Enter the image URL');
      if (value) {
        editor.insertEmbed(range.index, 'image', value);
      }
    }
  };

  const sourceClickHandler = () => {
    if(!showSource) {
      setSourceContent(quillRef.current?.getEditor().root.innerHTML);
    }
    else {
      let editor = quillRef.current?.getEditor();
      if (editor) {
        editor.clipboard.dangerouslyPasteHTML(sourceContent);
        toast.success('Source applied');
      } else alert('No editor found');
    }

    setShowSource(!showSource);
  };

  const buildToolBar = () => {
    switch (props.options) {
      case 'full':
        return (
          <>
            <div className="ql-toolbar ql-snow" id={`menu-${id}`}>
              <span className="ql-formats">
                <button type="button" className="ql-bold"></button>
                <button type="button" className="ql-italic"></button>
                <button type="button" className="ql-underline"></button>
                <button type="button" className="ql-strike"></button>
              </span>
              <span className="ql-formats">
                {!props.hideAlign && (
                  <select className="ql-align">
                    <option selected={true}></option>
                    <option value="center"></option>
                    <option value="right"></option>
                    <option value="justify"></option>
                  </select>
                )}
                <select className="ql-size">
                  <option value="small"></option>
                  <option selected={true}></option>
                  <option value="large"></option>
                  <option value="huge"></option>
                </select>
                <select className="ql-header">
                  <option value="1"></option>
                  <option value="2"></option>
                  <option value="3"></option>
                  <option value="4"></option>
                  <option value="5"></option>
                  <option value="6"></option>
                  <option selected={true}></option>
                </select>
              </span>
              <span className="ql-formats">
                <button type="button" className="ql-list" value="ordered"></button>
                <button type="button" className="ql-list" value="bullet"></button>
                <button type="button" className="ql-list" value="check"></button>
              </span>
              <span className="ql-formats">
                <button type="button" className="ql-indent" value="-1"></button>
                <button type="button" className="ql-indent" value="+1"></button>
              </span>
              <span className="ql-formats">
                <button type="button" className="ql-link"></button>
                <button type="button" className="ql-image"></button>
                <button type="button" className="ql-video"></button>
              </span>
              <span className="ql-formats">
                <button type="button" className="ql-clean"></button>
              </span>
              <span className="ql-formats">
                <button type="button" className="ql-source"><Icon type={IconType.code}/></button>
              </span>
              {props.hideExpand ? false : true && <span className="ql-formats">
                    <button type="button" className="ql-expand">
                      <Icon type={IconType.expand} />
                    </button> 
                    <button type="button" className="ql-colapse">
                      <Icon type={IconType.compress} />
                    </button>
                  </span>}
            </div>
          </>
        );
      case 'simple':
          return (
            <>
              <div className="ql-toolbar ql-snow" id={`menu-${id}`}>
                <span className="ql-formats">
                  <button type="button" className="ql-bold"></button>
                  <button type="button" className="ql-italic"></button>
                  <button type="button" className="ql-underline"></button>
                  <button type="button" className="ql-strike"></button>
                </span>
                {!props.hideAlign && (
                  <span className="ql-formats">
                    <select className="ql-align">
                      <option selected={true}></option>
                      <option value="center"></option>
                      <option value="right"></option>
                      <option value="justify"></option>
                    </select>
                  </span>
                )}
                <span className="ql-formats">
                  <button type="button" className="ql-list" value="ordered"></button>
                  <button type="button" className="ql-list" value="bullet"></button>
                  <button type="button" className="ql-list" value="check"></button>
                </span>
                <span className="ql-formats">
                  <button type="button" className="ql-clean"></button>
                </span>
                {props.hideExpand ? false : true && <span className="ql-formats">
                    <button type="button" className="ql-expand">
                      <Icon type={IconType.expand} />
                    </button>
                    <button type="button" className="ql-colapse">
                      <Icon type={IconType.compress} />
                    </button>
                  </span>}
              </div>
            </>
          );
    }
  };

  const modules = useMemo(() => {
    return {
      toolbar: {
        container: `#menu-${id}`,
        handlers: {
          image: imageClickHandler,
          source: sourceClickHandler,
          expand: () => { setExpanded(true); },
          colapse: () => { setExpanded(false);},
        },
      },
    };
  }, [props.options]);

  const getHeightClass = () => {
    if (props.height === 'short') {
      return 'editor-short';
    }
    return 'editor-normal';
  };

  return (
    <>
      <div
        className={expanded ? 'wysiwg-expanded' : ''}
        onMouseDown={() => {
          setExpanded(false);
        }}>
        <div
          className="wysiwg-container"
          onMouseDown={(e) => {
            e.stopPropagation();
          }}>
          {buildToolBar()}
          <Suspense fallback={<div className="bumper">Loading editor...</div>}>
          <ReactQuill theme="snow" 
          value={editorState} 
          onChange={(x)=>{setEditorState(x); props.changed?.(x)}} 
          className={`wysiwyg ${getHeightClass()}`} 
          onBlur={onBlur} 
          ref={quillRef} 
          modules={modules}
          placeholder={props.placeholder}
           />
          </Suspense>

          {props.saveBeforeChange && (
            <button type="button" className="btn btn-secondary" onClick={savePress}>
              <Icon type={IconType.save} /> Save{' '}
            </button>
          )}
          {props.close && props.saveBeforeChange && (
            <button type="button" className="btn btn-default" onClick={closeSavePress}>
              <Icon type={IconType.save} /> Save & Close{' '}
            </button>
          )}
          {props.close && (
            <button type="button" className="btn btn-default" onClick={closePress}>
              <Icon type={IconType.close} /> Close{' '}
            </button>
          )}
        </div>
      </div>

      {showSource && sourceContent && <Modal title='View Source' setModalOpen={sourceClickHandler} size='xxl'>
          <Suspense fallback={<div className="bumper">Loading editor...</div>}>
          <XEditor valueChanged={(x) => {
            setSourceContent(x);
          }} value={sourceContent} type='html' className='page-editor-view-source' />
          </Suspense>
        </Modal>}
    </>
  );
};

export default Editor;

export { FilePicker } from './FilePicker';
export type { IfilePickerResponse, IClientFile } from './FilePicker';
